import { ReactElement, useMemo, useRef, useState } from 'react';
import {
    UIButton,
    UIIcon,
    UIMenu,
    UIMenuDivider,
    UIMenuItem,
    UIPopover,
    UIPressableAreaButtonLess,
    UIPropoverRef,
    UIText,
    useUIConfirmationModal
} from '@/ui';
import { backgroundMedium, charcoal, concrete, red } from '@/ui/styles';
import fallIcon from '../assets/images/fall.png';
import tiltIcon from '../assets/images/tilt.png';
import geofenceIcon from '../assets/images/geofence.png';
import { Device } from '@/api';
import {
    useBulkCancelDeviceFallAlertAction,
    useBulkCancelDeviceGeoAlertAction,
    useBulkCancelDevicePowerOffAction,
    useBulkCancelDeviceRebootAction,
    useBulkCancelDeviceTiltAlertAction,
    useBulkCancelDeviceWifiAction,
    useBulkPowerOffDevicesAction,
    useBulkRebootDevicesAction
} from '../actions';
import { useDevicesTranslation } from '../hooks';
import { useBulkAlertLicenses } from '../queries';
import { deriveDevicePendingChanges } from '../util/device-pending-changes';
import { BulkFallModal } from './bulk-fall-modal';
import { BulkGeofenceModal } from './bulk-geofence-modal';
import { BulkSetTeamModal } from './bulk-set-team-modal';
import { BulkTiltModal } from './bulk-tilt-modal';
import { BulkWifiModal } from './bulk-wifi-modal';
import styles from './bulk-action-bar.module.scss';

type BulkAction = 'team' | 'fall' | 'tilt' | 'geofence' | 'wifi';

/** A pending change that can be cancelled across some of the selected devices. */
type CancelEntry = {
    key: string;
    label: string;
    /** Only the selected devices that actually have this change pending. */
    deviceIds: string[];
    run: (deviceIds: string[]) => Promise<unknown>;
};

export function BulkActionBar(props: {
    companyId: string;
    devices: Device[];
    onClear: () => void;
}): ReactElement {
    const { companyId, devices, onClear } = props;
    const { t } = useDevicesTranslation();
    const actionsRef = useRef<UIPropoverRef>(null);
    const cancelRef = useRef<UIPropoverRef>(null);
    const confirmationModal = useUIConfirmationModal();
    const [bulkAction, setBulkAction] = useState<BulkAction | null>(null);

    const { bulkRebootDevices } = useBulkRebootDevicesAction({ companyId });
    const { bulkPowerOffDevices } = useBulkPowerOffDevicesAction({ companyId });
    const { bulkCancelDeviceReboot } = useBulkCancelDeviceRebootAction({
        companyId
    });
    const { bulkCancelDevicePowerOff } = useBulkCancelDevicePowerOffAction({
        companyId
    });
    const { bulkCancelDeviceFallAlert } = useBulkCancelDeviceFallAlertAction({
        companyId
    });
    const { bulkCancelDeviceTiltAlert } = useBulkCancelDeviceTiltAlertAction({
        companyId
    });
    const { bulkCancelDeviceGeoAlert } = useBulkCancelDeviceGeoAlertAction({
        companyId
    });
    const { bulkCancelDeviceWifi } = useBulkCancelDeviceWifiAction({
        companyId
    });

    const deviceIds = useMemo(() => devices.map((d) => d.id), [devices]);
    const licenses = useBulkAlertLicenses({ companyId, deviceIds });

    // Disables a fall/tilt/geofence menu item when the feature can't be enabled
    // on the selection: usage hasn't loaded yet, the plan has no licences for it
    // (limit 0), or all licences are already in use and a selected device would
    // need a new one. Partial cases (some can be enabled) are handled by the
    // modal's message.
    // `unavailableText` is a whole sentence per feature rather than a template
    // with the feature name slotted in: a translator needs the article and
    // gender to agree with the noun, which an interpolated label cannot supply.
    function featureLockProps(
        availability: { limit: number; free: number; toEnable: number },
        unavailableText: string
    ): { disabled?: boolean; disabledTooltip?: string } {
        if (licenses.loading) {
            return { disabled: true };
        }
        if (availability.limit === 0) {
            return {
                disabled: true,
                disabledTooltip: unavailableText
            };
        }
        if (availability.free === 0 && availability.toEnable > 0) {
            return {
                disabled: true,
                disabledTooltip: t('shared.devices.feature_limit_reached')
            };
        }
        return {};
    }

    // Pending changes across the selection. Fall/tilt/reboot/power-off cancel
    // the single pending change; geofence/wifi are per-slot, so we surface one
    // entry per pending slot index. Each entry targets only the devices that
    // actually have that change pending.
    const cancelEntries = useMemo<CancelEntry[]>(() => {
        const fall: string[] = [];
        const tilt: string[] = [];
        const rebootIds: string[] = [];
        const powerOffIds: string[] = [];
        const geoBySlot = new Map<number, string[]>();
        const wifiBySlot = new Map<number, string[]>();

        const addSlot = (
            map: Map<number, string[]>,
            slots: number[],
            id: string
        ) =>
            slots.forEach((slot) => {
                const ids = map.get(slot) ?? [];
                ids.push(id);
                map.set(slot, ids);
            });

        devices.forEach((d) => {
            const pending = deriveDevicePendingChanges(d);
            if (pending.fall) {
                fall.push(d.id);
            }
            if (pending.tilt) {
                tilt.push(d.id);
            }
            if (pending.reboot) {
                rebootIds.push(d.id);
            }
            if (pending.powerOff) {
                powerOffIds.push(d.id);
            }
            addSlot(geoBySlot, pending.geoSlots, d.id);
            addSlot(wifiBySlot, pending.wifiSlots, d.id);
        });

        const entries: CancelEntry[] = [];

        if (fall.length > 0) {
            entries.push({
                key: 'fall',
                label: t('shared.devices.fall_short_label'),
                deviceIds: fall,
                run: (ids) => bulkCancelDeviceFallAlert({ deviceIds: ids })
            });
        }

        if (tilt.length > 0) {
            entries.push({
                key: 'tilt',
                label: t('shared.devices.tilt_short_label'),
                deviceIds: tilt,
                run: (ids) => bulkCancelDeviceTiltAlert({ deviceIds: ids })
            });
        }

        [...geoBySlot.entries()]
            .sort(([a], [b]) => a - b)
            .forEach(([slot, ids]) => {
                entries.push({
                    key: `geo-${slot}`,
                    label: t('shared.devices.geofence_slot_label', {
                        number: slot + 1
                    }),
                    deviceIds: ids,
                    run: (targets) =>
                        bulkCancelDeviceGeoAlert({ deviceIds: targets, slot })
                });
            });

        [...wifiBySlot.entries()]
            .sort(([a], [b]) => a - b)
            .forEach(([slot, ids]) => {
                entries.push({
                    key: `wifi-${slot}`,
                    label: t('shared.devices.wifi_slot_label', {
                        number: slot + 1
                    }),
                    deviceIds: ids,
                    run: (targets) =>
                        bulkCancelDeviceWifi({ deviceIds: targets, slot })
                });
            });

        if (rebootIds.length > 0) {
            entries.push({
                key: 'reboot',
                label: t('shared.devices.reboot_label'),
                deviceIds: rebootIds,
                run: (ids) => bulkCancelDeviceReboot({ deviceIds: ids })
            });
        }

        if (powerOffIds.length > 0) {
            entries.push({
                key: 'power_off',
                label: t('shared.devices.power_off_label'),
                deviceIds: powerOffIds,
                run: (ids) => bulkCancelDevicePowerOff({ deviceIds: ids })
            });
        }

        return entries;
    }, [
        devices,
        t,
        bulkCancelDeviceFallAlert,
        bulkCancelDeviceTiltAlert,
        bulkCancelDeviceGeoAlert,
        bulkCancelDeviceWifi,
        bulkCancelDeviceReboot,
        bulkCancelDevicePowerOff
    ]);

    function openModal(action: BulkAction) {
        actionsRef.current?.close();
        setBulkAction(action);
    }

    function handleApplied() {
        onClear();
        setBulkAction(null);
    }

    function confirm(opts: {
        title: string;
        confirmText: string;
        confirmVariant: 'primary' | 'danger';
        run: () => Promise<unknown>;
    }) {
        confirmationModal.open({
            title: opts.title,
            confirmText: opts.confirmText,
            confirmVariant: opts.confirmVariant,
            cancelText: t('shared.labels.cancel'),
            onConfirm: () => {
                confirmationModal.setLoading(true);
                opts.run()
                    .then(() => onClear())
                    .catch(() => {}) // Error handled in the action
                    .finally(() => confirmationModal.close());
            },
            onCancel: () => confirmationModal.close()
        });
    }

    function handleReboot() {
        actionsRef.current?.close();
        confirm({
            title: t('components.bulk_action_bar.reboot_confirm_title', {
                count: deviceIds.length
            }),
            confirmText: t('shared.devices.reboot_label'),
            confirmVariant: 'primary',
            run: () => bulkRebootDevices({ deviceIds })
        });
    }

    function handlePowerOff() {
        actionsRef.current?.close();
        confirm({
            title: t('components.bulk_action_bar.power_off_confirm_title', {
                count: deviceIds.length
            }),
            confirmText: t('shared.devices.power_off_label'),
            confirmVariant: 'danger',
            run: () => bulkPowerOffDevices({ deviceIds })
        });
    }

    function handleCancel(entry: CancelEntry) {
        cancelRef.current?.close();
        confirm({
            title: t(
                'components.bulk_action_bar.cancel_pending_confirm_title',
                {
                    count: entry.deviceIds.length
                }
            ),
            confirmText: t('shared.devices.cancel_pending_confirm'),
            confirmVariant: 'danger',
            run: () => entry.run(entry.deviceIds)
        });
    }

    return (
        <>
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: 8,
                    flexWrap: 'wrap',
                    minHeight: 40,
                    boxSizing: 'border-box',
                    padding: '8px 16px',
                    borderRadius: 8,
                    border: `1px solid ${concrete}`,
                    backgroundColor: backgroundMedium
                }}
            >
                <UIText medium>
                    {t('components.bulk_action_bar.selected', {
                        count: deviceIds.length
                    })}
                </UIText>
                <div style={{ flex: 1 }} />
                {cancelEntries.length > 0 && (
                    <UIPopover
                        ref={cancelRef}
                        placement="bottom_right"
                        popoverPadding={4}
                        trigger="click"
                        triggerNode={
                            <button className={styles.removeButtonStyle}>
                                <UIPressableAreaButtonLess
                                    verticalPadding={6}
                                    horizontalPadding={10}
                                >
                                    <UIText>
                                        {t(
                                            'components.bulk_action_bar.cancel_pending_label'
                                        )}
                                    </UIText>
                                    <UIIcon
                                        name={'chevron-down'}
                                        size={18}
                                        color={charcoal}
                                    />
                                </UIPressableAreaButtonLess>
                            </button>
                        }
                    >
                        <UIMenu>
                            {cancelEntries.map((entry) => (
                                <UIMenuItem
                                    key={entry.key}
                                    text={entry.label}
                                    leftIconName="x"
                                    onClick={() => handleCancel(entry)}
                                />
                            ))}
                        </UIMenu>
                    </UIPopover>
                )}
                <UIPopover
                    ref={actionsRef}
                    placement="bottom_right"
                    popoverPadding={4}
                    trigger="click"
                    triggerNode={
                        <button className={styles.removeButtonStyle}>
                            <UIPressableAreaButtonLess
                                verticalPadding={6}
                                horizontalPadding={10}
                            >
                                <UIText>
                                    {t('components.bulk_action_bar.menu_label')}
                                </UIText>
                                <UIIcon
                                    name={'chevron-down'}
                                    size={18}
                                    color={charcoal}
                                />
                            </UIPressableAreaButtonLess>
                        </button>
                    }
                >
                    <UIMenu>
                        <UIMenuItem
                            text={t('shared.devices.set_team_label')}
                            leftIconName="users"
                            onClick={() => openModal('team')}
                        />
                        <UIMenuItem
                            text={t('shared.devices.fall_short_label')}
                            leftImageProps={{
                                src: fallIcon,
                                alt: t('shared.devices.fall_short_label'),
                                width: 18
                            }}
                            onClick={() => openModal('fall')}
                            {...featureLockProps(
                                licenses.fall,
                                t('shared.devices.fall_unavailable')
                            )}
                        />
                        <UIMenuItem
                            text={t('shared.devices.tilt_short_label')}
                            leftImageProps={{
                                src: tiltIcon,
                                alt: t('shared.devices.tilt_short_label'),
                                width: 18
                            }}
                            onClick={() => openModal('tilt')}
                            {...featureLockProps(
                                licenses.tilt,
                                t('shared.devices.tilt_unavailable')
                            )}
                        />
                        <UIMenuItem
                            text={t('shared.devices.geofence_short_label')}
                            leftImageProps={{
                                src: geofenceIcon,
                                alt: t('shared.devices.geofence_short_label'),
                                width: 18
                            }}
                            onClick={() => openModal('geofence')}
                            {...featureLockProps(
                                licenses.geo,
                                t('shared.devices.geofence_unavailable')
                            )}
                        />
                        <UIMenuItem
                            text={t('shared.devices.wifi_label')}
                            leftIconName="wifi"
                            onClick={() => openModal('wifi')}
                        />
                        <UIMenuDivider />
                        <UIMenuItem
                            text={t('shared.devices.reboot_label')}
                            leftIconName="refresh-cw"
                            onClick={handleReboot}
                        />
                        <UIMenuItem
                            text={t('shared.devices.power_off_label')}
                            leftIconName="power"
                            textColor={red}
                            onClick={handlePowerOff}
                        />
                    </UIMenu>
                </UIPopover>
                <UIButton
                    variant="textDanger"
                    leftIcon="x"
                    text={t('shared.labels.clear')}
                    onClick={onClear}
                />
            </div>
            <BulkSetTeamModal
                companyId={companyId}
                deviceIds={deviceIds}
                isOpen={bulkAction === 'team'}
                onClose={() => setBulkAction(null)}
                onApplied={handleApplied}
            />
            <BulkFallModal
                companyId={companyId}
                deviceIds={deviceIds}
                isOpen={bulkAction === 'fall'}
                onClose={() => setBulkAction(null)}
                onApplied={handleApplied}
            />
            <BulkTiltModal
                companyId={companyId}
                deviceIds={deviceIds}
                isOpen={bulkAction === 'tilt'}
                onClose={() => setBulkAction(null)}
                onApplied={handleApplied}
            />
            <BulkGeofenceModal
                companyId={companyId}
                deviceIds={deviceIds}
                isOpen={bulkAction === 'geofence'}
                onClose={() => setBulkAction(null)}
                onApplied={handleApplied}
            />
            <BulkWifiModal
                companyId={companyId}
                deviceIds={deviceIds}
                isOpen={bulkAction === 'wifi'}
                onClose={() => setBulkAction(null)}
                onApplied={handleApplied}
            />
        </>
    );
}
